import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Shield, Loader2, CheckCircle2, XCircle, FileText, PenSquare, Download, ExternalLink } from 'lucide-react';
import api, { API } from '../api';
import { useToast } from '../hooks/use-toast';
import { useLang } from '../i18n/LanguageContext';

/**
 * Client-facing mandate consultation and acceptance page.
 * Two acceptance methods:
 *  1. DocuSign eSignature (embedded iframe or redirect) — preferred, legally strong.
 *  2. Internal acceptance (name + company + email + IP + timestamp) — fallback.
 */
export default function MandatePage() {
  const { rfqId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';
  const { toast } = useToast();
  const { lang, t } = useLang();

  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [signOpen, setSignOpen] = useState(false);
  const [method, setMethod] = useState('docusign');
  const [read, setRead] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerCompany, setSignerCompany] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [dsUrl, setDsUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await api.get(`/client/mandate/${rfqId}?token=${encodeURIComponent(token)}`);
      setState({ loading: false, error: '', data: res.data });
      const c = res.data?.rfq?.contact || {};
      setSignerName((prev) => prev || c.name || '');
      setSignerCompany((prev) => prev || c.company || '');
      setSignerEmail((prev) => prev || c.email || '');
    } catch (e) {
      setState({ loading: false, error: e?.response?.data?.detail || 'Lien invalide ou expiré.', data: null });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rfqId, token]);

  const isAccepted = state.data?.mandate?.status === 'accepted';
  const isReturnFromDs = sp.get('event') === 'signing_complete' || window.location.pathname.endsWith('/signature-complete');

  // Auto-finalize if we're coming back from DocuSign
  useEffect(() => {
    if (isReturnFromDs && !isAccepted && state.data) {
      finalizeDocusign(true);
    }
    // eslint-disable-next-line
  }, [isReturnFromDs, state.data?.mandate?.id]);

  const startDocusign = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/client/mandate/${rfqId}/docusign/start?token=${encodeURIComponent(token)}`);
      setDsUrl(res.data.signingUrl);
      // Optional: full redirect to DocuSign, or open in new tab
      window.location.href = res.data.signingUrl;
    } catch (e) {
      toast({ title: 'DocuSign', description: e?.response?.data?.detail || 'Impossible de démarrer la signature.', duration: 15000 });
      setBusy(false);
    }
  };

  const finalizeDocusign = async (silent = false) => {
    try {
      const res = await api.post(`/client/mandate/${rfqId}/docusign/finalize?token=${encodeURIComponent(token)}`);
      if (res.data?.status === 'accepted') {
        toast({ title: 'Mandat signé', description: 'Merci ! Votre mandat a été signé et enregistré.', duration: 10000 });
        load();
      } else if (!silent) {
        toast({ title: 'Signature en attente', description: 'La signature n\'est pas encore finalisée côté DocuSign.', duration: 8000 });
      }
    } catch (e) {
      if (!silent) toast({ title: 'DocuSign', description: e?.response?.data?.detail || 'Vérification impossible.', duration: 10000 });
    }
  };

  const acceptInternal = async () => {
    if (!read || !signerName.trim() || !signerCompany.trim() || !signerEmail.trim()) {
      toast({ title: t('mandate.missing'), description: t('mandate.missingDesc'), duration: 8000 });
      return;
    }
    setBusy(true);
    try {
      await api.post(`/client/mandate/${rfqId}/accept-internal?token=${encodeURIComponent(token)}`, {
        signerName, signerCompany, signerEmail, readConfirmed: read,
      });
      toast({ title: t('mandate.acceptedTitle'), description: t('mandate.acceptedDesc'), duration: 10000 });
      setSignOpen(false);
      load();
    } catch (e) {
      toast({ title: t('mandate.error'), description: e?.response?.data?.detail || t('mandate.errorDesc'), duration: 10000 });
    } finally { setBusy(false); }
  };

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-8 h-8 animate-spin text-brand-yellow" /></div>;
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
        <div className="max-w-md text-center bg-white p-8 rounded-xl shadow-md border border-gray-200">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-brand-navy mb-2">{t('mandate.errAccess')}</h1>
          <p className="text-gray-600 text-sm mb-6">{state.error}</p>
          <Link to="/" className="inline-flex items-center gap-2 text-brand-navy hover:text-brand-yellow text-sm font-semibold">← {t('mandate.back')}</Link>
        </div>
      </div>
    );
  }

  const m = state.data.mandate;
  const c = state.data.rfq?.contact || {};
  const pdfUrl = `${API}/client/mandate/${rfqId}/pdf?token=${encodeURIComponent(token)}`;
  const dateLocale = lang === 'zh' ? 'zh-CN' : lang === 'en' ? 'en-CA' : 'fr-CA';

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-brand-navy text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-8">
          <Link to="/" className="text-brand-yellow text-xs font-bold tracking-widest uppercase">← Yuville International</Link>
          <div className="inline-flex items-center gap-2 mt-3 mb-2 px-3 py-1 rounded-full bg-brand-yellow/20 border border-brand-yellow text-brand-yellow text-[11px] font-bold tracking-widest uppercase">
            <Shield className="w-3 h-3" /> {t('mandate.badge')}
          </div>
          <h1 data-testid="mandate-title" className="text-3xl md:text-4xl font-bold leading-tight">{t('mandate.headerTitle')}</h1>
          <p className="mt-3 text-white/80 max-w-2xl leading-relaxed" dangerouslySetInnerHTML={{ __html: t('mandate.headerBody') }} />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 lg:px-10 -mt-4">
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-5 grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand-yellow font-bold">{t('mandate.client')}</div>
            <div className="text-brand-navy font-semibold">{c.name}</div>
            {c.company && <div className="text-gray-500 text-xs">{c.company}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand-yellow font-bold">{t('mandate.project')}</div>
            <div className="text-brand-navy">{state.data.rfq?.need?.product || '—'}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand-yellow font-bold">{t('mandate.statusLabel')}</div>
            <StatusBadge status={m.status} t={t} />
          </div>
        </div>
      </div>

      {isAccepted && (
        <div className="max-w-4xl mx-auto px-6 lg:px-10 mt-6">
          <div className="bg-green-50 border-2 border-green-400 rounded-xl p-6" data-testid="mandate-accepted-banner">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h2 className="font-bold text-brand-navy text-lg">{t('mandate.acceptedTitle')}</h2>
                <p className="text-sm text-gray-700 mt-1">
                  {t('mandate.acceptedOn')} {new Date(m.acceptedAt).toLocaleString(dateLocale)} — {t('mandate.acceptedMethod')} : {m.method === 'docusign' ? t('mandate.methodDocusign') : t('mandate.methodInternal')}.
                </p>
                <a href={pdfUrl} className="inline-flex items-center gap-1.5 mt-3 text-brand-navy hover:text-brand-yellow text-sm font-semibold underline" download>
                  <Download className="w-4 h-4" /> {t('mandate.downloadSigned')}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="max-w-4xl mx-auto px-6 lg:px-10 mt-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-brand-navy/5 border-b border-gray-100 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-yellow font-bold">{t('mandate.docLabel')} · v{m.version}</div>
              <h2 className="text-brand-navy font-bold text-lg">{m.title}</h2>
            </div>
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-brand-navy hover:text-brand-yellow text-sm font-semibold" data-testid="download-mandate-pdf">
              <FileText className="w-4 h-4" /> {t('mandate.openPdf')} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="p-6 md:p-10 mandate-content max-h-[70vh] overflow-y-auto" data-testid="mandate-content">
            <div className="prose max-w-none text-brand-navy" dangerouslySetInnerHTML={{ __html: m.content }} />
          </div>
        </div>
      </section>

      {!isAccepted && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.05)] z-40">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-sm text-gray-600">{t('mandate.signPrompt')}</div>
            <button data-testid="open-sign-flow-btn" onClick={() => setSignOpen(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-yellow text-brand-navy text-sm font-bold hover:bg-yellow-400 transition">
              <PenSquare className="w-4 h-4" /> {t('mandate.signBtn')}
            </button>
          </div>
        </div>
      )}

      {signOpen && !isAccepted && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !busy && setSignOpen(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-brand-navy text-white px-6 py-4 flex items-center justify-between">
              <h2 className="font-bold">{t('mandate.signDialogTitle')}</h2>
              <button disabled={busy} onClick={() => setSignOpen(false)} className="text-white/80 hover:text-white disabled:opacity-40"><XCircle className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm text-gray-600">{t('mandate.chooseMethod')}</div>
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="method-docusign-btn" onClick={() => setMethod('docusign')} className={`p-3 rounded-lg border text-left transition ${method === 'docusign' ? 'border-brand-navy bg-brand-navy/5' : 'border-gray-200 hover:border-brand-navy'}`}>
                  <div className="font-bold text-brand-navy text-sm">{t('mandate.methodDocusign')}</div>
                  <div className="text-[11px] text-gray-500">{t('mandate.methodDocusignHint')}</div>
                </button>
                <button data-testid="method-internal-btn" onClick={() => setMethod('internal')} className={`p-3 rounded-lg border text-left transition ${method === 'internal' ? 'border-brand-navy bg-brand-navy/5' : 'border-gray-200 hover:border-brand-navy'}`}>
                  <div className="font-bold text-brand-navy text-sm">{t('mandate.methodInternal')}</div>
                  <div className="text-[11px] text-gray-500">{t('mandate.methodInternalHint')}</div>
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 pt-2">
                <input data-testid="signer-name-input" value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder={t('mandate.phName')} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-navy" />
                <input data-testid="signer-company-input" value={signerCompany} onChange={(e) => setSignerCompany(e.target.value)} placeholder={t('mandate.phCompany')} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-navy" />
                <input data-testid="signer-email-input" type="email" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} placeholder={t('mandate.phEmail')} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-navy" />
              </div>
              <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
                <input data-testid="read-confirmed-check" type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} className="w-4 h-4 mt-0.5 accent-brand-yellow" />
                <span>{t('mandate.readConfirm')}</span>
              </label>
              <div className="flex gap-2 pt-2">
                <button data-testid="cancel-sign-btn" disabled={busy} onClick={() => setSignOpen(false)} className="flex-1 px-4 py-2.5 rounded-full border border-gray-300 text-brand-navy text-sm font-semibold hover:bg-gray-50 disabled:opacity-50">{t('mandate.cancel')}</button>
                {method === 'docusign' ? (
                  <button data-testid="submit-docusign-btn" disabled={busy || !read || !signerEmail} onClick={startDocusign} className="flex-1 px-4 py-2.5 rounded-full bg-brand-yellow text-brand-navy text-sm font-bold hover:bg-yellow-400 disabled:opacity-50 flex items-center justify-center gap-2">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenSquare className="w-4 h-4" />} {t('mandate.submitDocusign')}
                  </button>
                ) : (
                  <button data-testid="submit-internal-btn" disabled={busy || !read || !signerName.trim() || !signerCompany.trim() || !signerEmail.trim()} onClick={acceptInternal} className="flex-1 px-4 py-2.5 rounded-full bg-brand-yellow text-brand-navy text-sm font-bold hover:bg-yellow-400 disabled:opacity-50 flex items-center justify-center gap-2">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {t('mandate.submitInternal')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status, t }) {
  const styleMap = {
    to_prepare: 'bg-gray-200 text-gray-700',
    sent: 'bg-blue-100 text-blue-700 border border-blue-300',
    waiting_acceptance: 'bg-amber-100 text-amber-800 border border-amber-400',
    accepted: 'bg-green-100 text-green-700 border border-green-400',
    refused: 'bg-red-100 text-red-700 border border-red-400',
    expired: 'bg-gray-200 text-gray-500 border border-gray-300',
    not_required: 'bg-gray-100 text-gray-500',
  };
  const label = t(`mandate.statuses.${status}`) || status;
  const c = styleMap[status] || 'bg-gray-100 text-gray-600';
  return <span data-testid="mandate-status-badge" className={`inline-block text-[11px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${c}`}>{label}</span>;
}
